"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useSession } from "next-auth/react";
import { BASE_PATH } from "@/constants";
import { GetMessageApiResponse, Message } from "@/types/inbox";

interface InboxContextValue {
  inbox: GetMessageApiResponse | null;
  messages: Message[];
  unreadCount: number;
  loading: boolean;

  fetchInbox: () => Promise<void>;
}

const InboxContext = createContext<InboxContextValue | undefined>(undefined);

export function InboxProvider({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const [inbox, setInbox] = useState<GetMessageApiResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchInbox = useCallback(async () => {
    if (status !== "authenticated") {
      setInbox(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${BASE_PATH}/api/inbox`, {
        cache: "no-store",
      });

      if (!res.ok) {
        throw new Error("Failed to fetch inbox");
      }

      const apiData = await res.json();
      // console.log("Inbox API response:", apiData);

      setInbox(apiData.data);
    } catch (error) {
      console.error("Inbox fetch error:", error);
      setInbox(null);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  return (
    <InboxContext.Provider
      value={{
        inbox,
        messages: inbox?.messages || [],
        unreadCount: inbox?.numOfUnreadMessages ?? 0,
        loading,
        fetchInbox,
      }}
    >
      {children}
    </InboxContext.Provider>
  );
}

export function useInbox() {
  const context = useContext(InboxContext);

  if (!context) {
    throw new Error("useInbox must be used within InboxProvider");
  }

  return context;
}
